import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { currentRole, isLivyAdmin } from 'src/components/livy/access';
import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Snackbar, Stack, Typography } from '@mui/material';
import { LivyPanel } from 'src/components/livy/LivyPanel';
import { LivyMarkdown } from 'src/components/livy/LivyMarkdown';
import { useLivyChat } from 'src/components/livy/useLivyChat';
import { extractDraft } from 'src/components/livy/draft';
import { livyConfig } from 'src/lib/livyTransport';
import { livyJson, freshToken } from 'src/lib/livyApi';
const STARTERS = ['A resident says their water charge doubled this month, what happened?', 'Customer asks why their statement has not gone out yet', 'How do I explain common area deductions to a resident?', 'This company says their AppFolio charges are missing'];

/**
 * Support triage. Livy reads the knowledge base and the account, then drafts a reply for a human.
 *
 * It has no tool that emails, messages or opens a ticket, and this page deliberately offers copy
 * rather than send: a support reply is the one output that reaches a customer with Livable's name
 * on it, so a person decides what is sent, every time.
 */
export function SupportTab() {
  var _localStorage$getItem;
  const companyLabel = (_localStorage$getItem = localStorage.getItem(USER_SETTINGS_KEYS.companyName)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : undefined;
  const isAdmin = isLivyAdmin(currentRole());
  const config = livyConfig();
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    copied = _useState2[0],
    setCopied = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    saved = _useState4[0],
    setSaved = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    saving = _useState6[0],
    setSaving = _useState6[1];
  // freshToken, not the raw store: a streaming turn signs its own request and never passes
  // through the app's refresh interceptor.
  const getToken = useCallback(() => freshToken(), []);
  const context = useMemo(() => ({
    route: '/livy/support'
  }), []);
  const _useLivyChat = useLivyChat({
      config: config !== null && config !== void 0 ? config : {
        endpoint: '',
        region: '',
        identityPoolId: '',
        userPoolId: ''
      },
      getToken,
      context,
      surface: 'support'
    }),
    messages = _useLivyChat.messages,
    streaming = _useLivyChat.streaming,
    error = _useLivyChat.error,
    send = _useLivyChat.send,
    stop = _useLivyChat.stop,
    retry = _useLivyChat.retry;
  const lastQuestion = useMemo(() => {
    var _reverse$find$content, _reverse$find;
    return (_reverse$find$content = (_reverse$find = [...messages].reverse().find(m => m.role === 'user')) === null || _reverse$find === void 0 ? void 0 : _reverse$find.content) !== null && _reverse$find$content !== void 0 ? _reverse$find$content : null;
  }, [messages]);

  /** Whether the answer actually consulted the knowledge base; drives the low-confidence rule. */
  // The turn being filed, which is the last assistant message and nothing earlier. Scanning back
  // for any message with a draft kept an old one actionable after a newer question had been asked,
  // so the filed draft could answer a different question from the one on screen.
  const currentAnswer = useMemo(() => {
    const last = messages[messages.length - 1];
    return (last === null || last === void 0 ? void 0 : last.role) === 'assistant' ? last : null;
  }, [messages]);
  const lastDraft = useMemo(() => currentAnswer ? extractDraft(currentAnswer.content).draft : null, [currentAnswer]);

  // Grounding is a property of the answer being filed, not of the conversation. A conversation-wide
  // check stayed true once any earlier answer had used the knowledge base, so a later ungrounded
  // draft was filed as grounded and skipped the low-confidence escalation this flag exists to drive.
  const usedKnowledge = useMemo(() => {
    var _currentAnswer$tools;
    return ((_currentAnswer$tools = currentAnswer === null || currentAnswer === void 0 ? void 0 : currentAnswer.tools) !== null && _currentAnswer$tools !== void 0 ? _currentAnswer$tools : []).some(t => t.startsWith('search_knowledge_base') || t.startsWith('get_kb_article'));
  }, [currentAnswer]);
  if (!isAdmin || !config) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        p: '24px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1"
    }, "Support triage is not available for this account yet."));
  }
  return /*#__PURE__*/React.createElement(Stack, {
    direction: {
      xs: 'column',
      lg: 'row'
    },
    sx: {
      height: '100%',
      minHeight: 0
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    sx: {
      flex: 1,
      minWidth: 0,
      minHeight: {
        xs: 460,
        lg: 0
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: '16px',
      py: '10px',
      borderBottom: 1,
      borderColor: 'divider'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 15,
      fontWeight: 600,
      letterSpacing: '-.01em'
    }
  }, "Support"), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "Livy drafts; you decide what the customer sees")), /*#__PURE__*/React.createElement(LivyPanel, {
    messages: messages,
    streaming: streaming,
    error: error,
    companyLabel: companyLabel,
    starters: STARTERS,
    onSend: send,
    onStop: stop,
    onRetry: retry
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: {
        xs: '100%',
        lg: 380
      },
      flex: '0 0 auto',
      borderLeft: {
        lg: 1
      },
      borderTop: {
        xs: 1,
        lg: 0
      },
      borderColor: 'divider',
      p: '16px',
      overflowY: 'auto',
      minHeight: {
        xs: 260,
        lg: 0
      }
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11,
      letterSpacing: '.07em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: '10px'
    }
  }, "Draft reply"), !lastDraft ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "Ask about a customer problem and the draft appears here, ready to review.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Paper, {
    variant: "outlined",
    sx: {
      p: '14px',
      borderRadius: '10px',
      mb: '12px'
    }
  }, /*#__PURE__*/React.createElement(LivyMarkdown, {
    content: lastDraft
  })), /*#__PURE__*/React.createElement(Stack, {
    spacing: "8px"
  }, /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    variant: "contained",
    sx: {
      textTransform: 'none'
    },
    onClick: () => {
      void navigator.clipboard.writeText(lastDraft);
      setCopied(true);
    }
  }, "Copy draft"), /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    variant: "outlined",
    disabled: saving,
    sx: {
      textTransform: 'none'
    },
    onClick: async () => {
      setSaving(true);
      try {
        // Shadow mode: the draft is filed against a thread and scored for escalation.
        // Nothing is delivered; this records what the assistant would have said.
        const _await$livyJson = await livyJson('/threads', {
            method: 'POST',
            body: {
              subject: lastQuestion !== null && lastQuestion !== void 0 ? lastQuestion : 'Support request'
            }
          }),
          thread = _await$livyJson.thread;
        const result = await livyJson("/threads/".concat(thread.threadId, "/messages"), {
          method: 'POST',
          body: {
            role: 'draft',
            body: lastDraft,
            text: lastQuestion,
            groundedInKb: usedKnowledge
          }
        });
        setSaved(result);
      } catch (_unused) {
        setSaved(null);
      } finally {
        setSaving(false);
      }
    }
  }, saving ? /*#__PURE__*/React.createElement(CircularProgress, {
    size: 16
  }) : 'File for review')), saved && saved.escalations.length > 0 && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    variant: "outlined",
    sx: {
      mt: '12px',
      fontSize: 12.5
    }
  }, "Needs a person:", /*#__PURE__*/React.createElement(Box, {
    component: "ul",
    sx: {
      pl: '18px',
      m: '6px 0 0'
    }
  }, saved.escalations.map(e => /*#__PURE__*/React.createElement("li", {
    key: e.reason
  }, e.reason)))), saved && saved.escalations.length === 0 && /*#__PURE__*/React.createElement(Alert, {
    severity: "success",
    variant: "outlined",
    sx: {
      mt: '12px',
      fontSize: 12.5
    }
  }, "Filed for review. No escalation rule fired."), /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    variant: "outlined",
    sx: {
      mt: '12px',
      fontSize: 12.5
    }
  }, "Nothing here has been sent. Read it, edit it, then send it yourself."))), /*#__PURE__*/React.createElement(Snackbar, {
    open: copied,
    autoHideDuration: 2500,
    onClose: () => setCopied(false),
    message: "Draft copied, nothing was sent"
  }));
}